import { Stack } from "@certn/cascadia";
import { Schemas } from "../../types";
import {
  DateSelector,
  SectionMessage,
  SectionSubHeading,
  TextField,
  Toggle,
} from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  formatDateSelectValueAsISODate,
  parseISODateAsDateSelectValue,
} from "@certn/cascadia-react/components/inputs/date-input/utils";
import { css } from "@emotion/css";
import { hasMonthPrecision } from "./utils";
import isNil from "lodash/isNil";

type CVReviewEducationExtractionProps = {
  education: Schemas["CVEducationItem"];
  originalEducation?: Schemas["CVEducationItem"];
  handleEducationItemChange: (education: Schemas["CVEducationItem"]) => void;
};

export const CVReviewEducationExtraction = ({
  education,
  originalEducation,
  handleEducationItemChange,
}: CVReviewEducationExtractionProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(
    isNil(education.is_excluded) ? true : !education.is_excluded
  );

  return (
    <Stack gap="space-2" className={css({ width: "100%" })}>
      <SectionSubHeading
        testID="education-subheading"
        title={education.institution_name}
        isCollapsible
        noWrap
        isOpen={open}
        onClick={() => setOpen(!open)}
        detailText={education.is_excluded ? t("Excluded") : undefined}
      />
      {open && (
        <Stack gap="space-4" className={css({ padding: "var(--space-2)" })}>
          {education.is_excluded && (
            <SectionMessage
              type="info"
              sectionTitle={t(
                "This education is outside the verification scope and will be excluded from the review."
              )}
              icon="info"
            />
          )}
          <TextField
            label={t("Institution")}
            name="institution_name"
            testID="education-item-institution"
            isRequired
            value={education.institution_name}
            onChange={(value) =>
              handleEducationItemChange({
                ...education,
                institution_name: value,
              })
            }
          />
          <TextField
            label={t("Designation")}
            name="designation"
            testID="education-item-designation"
            value={education.designation || undefined}
            onChange={(value) =>
              handleEducationItemChange({
                ...education,
                designation: value,
              })
            }
          />
          <TextField
            label={t("Subject / Field of study")}
            name="subject"
            testID="education-item-subject"
            value={education.subject || undefined}
            onChange={(value) =>
              handleEducationItemChange({
                ...education,
                subject: value,
              })
            }
          />
          <Stack
            gap="space-2"
            orientation="horizontal"
            className={css({ width: "100%", "& > *": { flex: 1 } })}
          >
            <DateSelector
              label={t("Start year")}
              value={
                parseISODateAsDateSelectValue(education.start_date) || undefined
              }
              onChange={(value) => {
                const start_date = formatDateSelectValueAsISODate(value);

                handleEducationItemChange({
                  ...education,
                  start_date: start_date || education.start_date,
                });
              }}
              testID="education-start-year"
              name="start_date"
              fullWidth
              formatOverride="yy"
            />
            <DateSelector
              label={t("Start month")}
              value={
                hasMonthPrecision(education.start_date_precision)
                  ? parseISODateAsDateSelectValue(education.start_date) ||
                    undefined
                  : undefined
              }
              onChange={(value) => {
                const start_date = formatDateSelectValueAsISODate(value);

                handleEducationItemChange({
                  ...education,
                  start_date: start_date || education.start_date,
                  start_date_precision: "month",
                });
              }}
              testID="education-start-month"
              name="start_date"
              fullWidth
              formatOverride="mm"
            />
          </Stack>
          {!education.is_current && (
            <Stack
              gap="space-2"
              orientation="horizontal"
              className={css({ width: "100%", "& > *": { flex: 1 } })}
            >
              <DateSelector
                label={t("End year")}
                value={
                  (education.end_date &&
                    parseISODateAsDateSelectValue(education.end_date)) ||
                  undefined
                }
                onChange={(value) => {
                  const end_date = formatDateSelectValueAsISODate(value);

                  handleEducationItemChange({
                    ...education,
                    end_date,
                  });
                }}
                testID="education-end-year"
                name="end_date"
                fullWidth
                formatOverride="yy"
              />
              <DateSelector
                label={t("End month")}
                value={
                  education.end_date_precision &&
                  hasMonthPrecision(education.end_date_precision)
                    ? (education.end_date &&
                        parseISODateAsDateSelectValue(education.end_date)) ||
                      undefined
                    : undefined
                }
                onChange={(value) => {
                  const end_date = formatDateSelectValueAsISODate(value);

                  handleEducationItemChange({
                    ...education,
                    end_date,
                    end_date_precision: "month",
                  });
                }}
                testID="education-end-month"
                name="end_date"
                fullWidth
                formatOverride="mm"
              />
            </Stack>
          )}
          <Toggle
            name="is_current"
            isSelected={education.is_current}
            onChange={(selected) => {
              handleEducationItemChange({
                ...education,
                is_current: selected,
                // Restore the original values
                ...(!selected &&
                  originalEducation && {
                    end_date: originalEducation.end_date,
                    end_date_precision: originalEducation.end_date_precision,
                  }),
              });
            }}
            testID="education-item-current"
          >
            {t("Currently attending")}
          </Toggle>
        </Stack>
      )}
    </Stack>
  );
};
